import util from '../../lib/util';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from "../find";
import Group from '../../entities/group';
import Role from '../../entities/role';
import Deadline from '../../entities/deadline';
import Task from '../../entities/task';
import TemplateTask from '../../entities/templateTask';

/*
 * Delete/nullify anywhere this group is used in other entities
 *
 * @param {Group} group
 */
export function deleteGroup(group: Group, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = group.id;
    }
    /* Simple delete usages */
        
    store.getState().roles
        .getMappingFor('groupId', group.id)
        .filter(roleTemp => roleTemp.status !== 'D')
        .forEach(roleTemp => {
            const _role = roleTemp.clone();
            _role.status =  'U';
            _role.addedOn = newHRTimestamp();
            _role.importSource = targetImportSource;
            _role.importId = mergeId;

            _role.groups = _role.groups?.filter(id => id !== group.id) || [];
            store.dispatch(Actions.RoleActions.saveRoleQueue(_role));
        });

    /* Complex delete usages */
        /* - Delete key/value usages */
    
    findAll<Deadline>('deadlines')
        .filter(deadlineTemp => deadlineTemp.assignedToType === 'G' && deadlineTemp.assignedTo === group.id && deadlineTemp.status !== 'D')
        .forEach(deadlineTemp => {
            const _deadline = deadlineTemp.clone();
            // Update the target with a null in the field (do NOT delete!)
            _deadline.status = 'U';
            _deadline.addedOn = newHRTimestamp();
            _deadline.addedBy = getCurrentUserId();
            _deadline.importSource = targetImportSource;
            _deadline.importId = mergeId;
            store.dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline));
        });

    findAll<Task>('tasks')
        .filter(taskTemp => taskTemp.assignedToType === 'G' && taskTemp.assignedTo === group.id && taskTemp.status !== 'D')
        .forEach(taskTemp => {
            const _task = taskTemp.clone();
            // Update the target with a null in the field (do NOT delete!)
            _task.status = 'U';
            _task.addedOn = newHRTimestamp();
            _task.addedBy = getCurrentUserId();
            _task.importSource = targetImportSource;
            _task.importId = mergeId;
            store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
        });

    findAll<TemplateTask>('templateTasks')
        .filter(templateTaskTemp => templateTaskTemp.assignedToType === 'G' && templateTaskTemp.assignedTo === group.id && templateTaskTemp.status !== 'D')
        .forEach(templateTaskTemp => {
            const _templateTask = templateTaskTemp.clone();
            // Delete the target
            _templateTask.status = 'D';
            _templateTask.addedOn = newHRTimestamp();
            _templateTask.addedBy = getCurrentUserId();
            _templateTask.importSource = targetImportSource;
            _templateTask.importId = mergeId;
            store.dispatch(Actions.TemplateTaskActions.saveTemplateTaskQueue(_templateTask));
        });

    /* Remove the actual group */
    group.status = 'D';
    group.addedOn = newHRTimestamp();
    group.addedBy = getCurrentUserId();
    group.importSource = targetImportSource;
    group.importId = mergeId;
    store.dispatch(Actions.GroupActions.removeGroupQueue(group));
    
}

/*
 * Replace anywhere this group is used in other entities.
 *
 * @param {Group} group
 * @param {Group} replaceWith
 * @return {Group} - Group will have refs stripped away
 */
export function replaceGroup(group: Group, replaceWith: Group, mergeId?: string) {
    let targetImportSource = 'merge';

    if (!mergeId) {
        targetImportSource = 'delete';
        mergeId = group.id;
    }
    /* Simple replace usages */
        
    store.getState().roles
        .getMappingFor('groupId', group.id)
        .filter(roleTemp => roleTemp.status !== 'D')
        .forEach(roleTemp => {
            const _role = roleTemp.clone();
            _role.status =  'U';
            _role.addedOn = newHRTimestamp();
            _role.importSource = targetImportSource;
            _role.importId = mergeId;

            _role.groups = _role.groups?.map(id => (id === group.id) ? replaceWith.id : id) || [];
            store.dispatch(Actions.RoleActions.saveRoleQueue(_role));
        });

    /* Complex replace usages */
        /* - Modify key/value usages */
    
    findAll<Deadline>('deadlines')
        .filter(deadlineTemp => deadlineTemp.assignedToType === 'G' && deadlineTemp.assignedTo === group.id && deadlineTemp.status !== 'D')
        .forEach(deadlineTemp => {
            const _deadline = deadlineTemp.clone();
            // Replace the target with the new ID
			_deadline.assignedTo = replaceWith.id;
            _deadline.status = 'U';
            _deadline.addedOn = newHRTimestamp();
            _deadline.addedBy = getCurrentUserId();
            _deadline.importSource = targetImportSource;
            _deadline.importId = mergeId;
            store.dispatch(Actions.DeadlineActions.saveDeadlineQueue(_deadline));
        });

    findAll<Task>('tasks')
        .filter(taskTemp => taskTemp.assignedToType === 'G' && taskTemp.assignedTo === group.id && taskTemp.status !== 'D')
        .forEach(taskTemp => {
            const _task = taskTemp.clone();
            // Replace the target with the new ID
			_task.assignedTo = replaceWith.id;
            _task.status = 'U';
            _task.addedOn = newHRTimestamp();
            _task.addedBy = getCurrentUserId();
            _task.importSource = targetImportSource;
            _task.importId = mergeId;
            store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
        });

    findAll<TemplateTask>('templateTasks')
        .filter(templateTaskTemp => templateTaskTemp.assignedToType === 'G' && templateTaskTemp.assignedTo === group.id && templateTaskTemp.status !== 'D')
        .forEach(templateTaskTemp => {
            const _templateTask = templateTaskTemp.clone();
            // Replace the target with the new ID
			_templateTask.assignedTo = replaceWith.id;
            _templateTask.status = 'U';
            _templateTask.addedOn = newHRTimestamp();
            _templateTask.addedBy = getCurrentUserId();
            _templateTask.importSource = targetImportSource;
            _templateTask.importId = mergeId;
            store.dispatch(Actions.TemplateTaskActions.saveTemplateTaskQueue(_templateTask));
        });
    return group;
}

/*
 * Determine the impact of deleting this group from other entities
 *
 * @param {Group} group
 * @return {Array.<{count: number, entity: string}>} - a list of impacted entities, along with a count of impacted entities
 */
export function deleteImpactGroup(group: Group) {
    const impacts: string[] = [];
    const state = store.getState();
    
    /* Simple delete usages */
        
    const roleGroupsImpact = state.roles
          .getMappingFor('groupId', group.id)
          .filter(role => role.status !== 'D');

    roleGroupsImpact.forEach(() => impacts.push('roles'));

    /* Complex delete usages */
        /* - Impact key/value usages */
    
    const deadlineAssignedToTypeImpact = findAll<Deadline>('deadlines')
        .filter(deadline => deadline.assignedToType === 'G' && deadline.assignedTo === group.id && deadline.status !== 'D');
    deadlineAssignedToTypeImpact.forEach(() => impacts.push('deadlines'));

    const taskAssignedToTypeImpact = findAll<Task>('tasks')
        .filter(task => task.assignedToType === 'G' && task.assignedTo === group.id && task.status !== 'D');
    taskAssignedToTypeImpact.forEach(() => impacts.push('tasks'));

    const templateTaskAssignedToTypeImpact = findAll<TemplateTask>('templateTasks')
        .filter(templateTask => templateTask.assignedToType === 'G' && templateTask.assignedTo === group.id && templateTask.status !== 'D');
    templateTaskAssignedToTypeImpact.forEach(() => impacts.push('templateTasks'));

    return util.countDistinct(impacts);
}